/**
 * One decision form for every review stage: who acts and why, what is
 * outstanding, the decisions, the comment and the action.
 *
 * @module components/governance/ReviewDecision
 */

import React from "react";

import { workflowActionAppearance } from "@/components/shared/workflow-queue-actions";
import { Button, Eyebrow, Text } from "@/components/ui";
import { FormField, TextAreaField } from "@/components/ui/Form";

export interface ReviewDecisionOption {
  id: string;
  label: string;
  icon: string;
  tone: "primary" | "outline" | "warning";
  /** Whether this decision is recorded with the comment, which it then requires. */
  requiresComment: boolean;
  /** Why it cannot be taken now, beyond a missing comment. Shown inline. */
  refusal?: string;
}

export interface ReviewDecisionProps {
  /** Who is acting and why, or whose turn it is when the reader has no decision. */
  actor: React.ReactNode;
  /** What is still outstanding, such as open readiness blockers. */
  outstanding?: React.ReactNode;
  decisions: readonly ReviewDecisionOption[];
  comment: string;
  onCommentChange: (comment: string) => void;
  /** Fields a decision needs beyond the comment. */
  children?: React.ReactNode;
  pending: boolean;
  onDecide: (decision: ReviewDecisionOption) => void;
}

export const COMMENT_HINT = "Add a comment to record a decision.";

export function ReviewDecision({
  actor,
  outstanding = undefined,
  decisions,
  comment,
  onCommentChange,
  children = undefined,
  pending,
  onDecide,
}: ReviewDecisionProps): React.ReactElement {
  const commented = comment.trim().length > 0;
  const takesComment = decisions.some((decision) => decision.requiresComment);
  const refusalOf = (decision: ReviewDecisionOption): string | undefined =>
    decision.refusal ?? (decision.requiresComment && !commented ? COMMENT_HINT : undefined);
  // The missing comment is said once, under the field; anything else per decision.
  const refused = decisions.filter((decision) => decision.refusal !== undefined);
  return (
    <div className="mt-4" data-testid="governance-workflow-actions">
      <Eyebrow as="div">{decisions.length > 0 ? "Available actions now" : "Next action"}</Eyebrow>
      <Text className="mt-1" data-testid="review-decision-actor">
        {actor}
      </Text>
      {outstanding}
      {takesComment ? (
        <FormField label="Comment" htmlFor="governance-workflow-note" className="mt-2" required>
          <TextAreaField
            id="governance-workflow-note"
            className="min-h-24"
            value={comment}
            onChange={onCommentChange}
            placeholder="Why you are taking this decision, for the audit trail"
            disabled={pending}
            disabledReason="Wait for the action in progress to finish."
            aria-describedby={commented ? undefined : "governance-workflow-note-hint"}
            data-testid="governance-workflow-note"
          />
        </FormField>
      ) : null}
      {takesComment && !commented ? (
        <Text
          id="governance-workflow-note-hint"
          tone="muted"
          className="mt-1"
          data-testid="governance-workflow-note-hint"
        >
          {COMMENT_HINT}
        </Text>
      ) : null}
      {children}
      {decisions.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {decisions.map((decision) => {
            const refusal = pending
              ? "Wait for the action in progress to finish."
              : refusalOf(decision);
            return (
              <Button
                key={decision.id}
                size="sm"
                {...workflowActionAppearance(decision.tone)}
                icon={decision.icon}
                loading={pending}
                data-testid={`governance-workflow-action-${decision.id}`}
                disabled={refusal !== undefined}
                disabledReason={refusal}
                onClick={() => onDecide(decision)}
              >
                {decision.label}
              </Button>
            );
          })}
        </div>
      ) : null}
      {refused.length > 0 ? (
        <Text as="div" tone="muted">
          <ul className="mt-2 space-y-1" data-testid="review-decision-refusals">
            {refused.map((decision) => (
              <li key={decision.id} data-testid={`review-decision-refusal-${decision.id}`}>
                <span className="font-semibold">{decision.label}:</span> {decision.refusal}
              </li>
            ))}
          </ul>
        </Text>
      ) : null}
    </div>
  );
}
